import React from "react";
import { Col, Form, Row, Button } from "react-bootstrap";
import { toastError, toastSuccess } from "../../helpers";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import * as yup from "yup";
import { useUpdateUserProfileMutation } from "../../app/features/apiSlice";
import CustomSelect from "../CustomSelect";

const PersonalDetails = ({ data, isLoading, error, refetch }) => {
  const { t } = useTranslation();

  const isCompany = data?.user?.orgUser?.type === "COMPANY";

  const initialValues = {
    sex: data?.user?.orgUser?.sex || "",
  };

  const schema = yup.object().shape({
    sex: yup.string().required("sexRequired"),
  });

  const [updateUser, { isLoading: isUpdating }] = useUpdateUserProfileMutation();

  const sexOptions = [
    { value: "MALE", label: t("form.man") },
    { value: "FEMALE", label: t("form.woman") },
    { value: "NOT DECLARED", label: t("form.nd") },
  ];

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = { sex: values.sex };
      const response = await updateUser(payload).unwrap();
      const message = response?.message ?? "Profile updated successfully";
      toastSuccess(message);
      await refetch();

    } catch (err) {
      const errorMessage = err?.data?.message || err?.message || t("error.serverNotResponding");
      toastError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    console.error("Data loading error:", error);
    return <div>Error loading personal details</div>;
  }

  return (
    <Formik
      validationSchema={schema}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      enableReinitialize={true}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
      }) => {
        return (
          <Form noValidate onSubmit={handleSubmit} className="add-inner-form">
            <h6 className="text-danger bold">
              {t("personalDetailsWarning")}
            </h6>
            {isCompany ? (
              <Form.Group as={Row} className="mb-3" controlId="formCompanyName">
                <Form.Label column xs={12} sm={2}>
                  {t("form.companyName")}
                </Form.Label>
                <Col xs={12} sm={10}>
                  <Form.Text className="form-control form-control-sm m-0">
                    {data?.user?.orgUser?.company_name}
                  </Form.Text>
                </Col>
              </Form.Group>
            ) : (
              <>
                <Form.Group as={Row} className="mb-3" controlId="formFirstName">
                  <Form.Label column xs={12} sm={2}>
                    {t("form.name")}
                  </Form.Label>
                  <Col xs={12} sm={10}>
                    <Form.Text className="form-control form-control-sm m-0">
                      {data?.user?.first_name}
                    </Form.Text>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-3" controlId="formLastName">
                  <Form.Label column xs={12} sm={2}>
                    {t("form.lastname")}
                  </Form.Label>
                  <Col xs={12} sm={10}>
                    <Form.Text className="form-control form-control-sm m-0">
                      {data?.user?.last_name}
                    </Form.Text>
                  </Col>
                </Form.Group>
              </>
            )}

            <Form.Group as={Row} className="mb-3" controlId="formSex">
              <Form.Label column xs={12} sm={2}>
                {t("form.sex")}
              </Form.Label>
              <Col xs={12} sm={10}>
                <CustomSelect
                  formik={{ values, errors, touched, setFieldValue, setFieldTouched }}
                  name="sex"
                  options={sexOptions}
                  placeholder={t("form.select")}
                  theme="light"
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3" controlId="formDob">
              <Form.Label column xs={12} sm={2}>
                {t("form.dob")}
              </Form.Label>
              <Col xs={12} sm={10}>
                <Form.Text className="form-control form-control-sm m-0">
                  {data?.user?.orgUser?.date_of_birth}
                </Form.Text>
              </Col>
            </Form.Group>
            <Button variant="primary-outline" type="submit" disabled={isSubmitting || isUpdating}>
              {t("update")}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PersonalDetails;